import { CHAIN_NAMESPACES, type ChainNamespaceType, CONFIRMATION_STRATEGY, createIdleTimeTracker } from "@toruslabs/base-controllers";
import { BUILD_ENV_TYPE, CITADEL_SERVER_MAP } from "@toruslabs/constants";
import {
  AccountAbstractionMultiChainConfig,
  GAS_ESTIMATE_TYPES,
  SUPPORTED_NETWORKS as SUPPORTED_NETWORKS_ETHEREUM,
} from "@toruslabs/ethereum-controllers";
import { SUPPORTED_NETWORKS as SUPPORTED_NETWORKS_SOLANA } from "@toruslabs/solana-controllers";

import appConfig from "../config";
import {
  type EthereumControllerConfig,
  type EthereumControllerState,
  type ITorusController,
  type ITorusControllerConfig,
  type ITorusControllerState,
  type SolanaControllerConfig,
  type SolanaControllerState,
} from "./interfaces";

const defaultSolanaChainId = appConfig.defaultChainIds[CHAIN_NAMESPACES.SOLANA];
const defaultSolanaProviderConfig = SUPPORTED_NETWORKS_SOLANA[defaultSolanaChainId];

const defaultEthereumChainId = appConfig.defaultChainIds[CHAIN_NAMESPACES.EIP155];
const defaultEthereumProviderConfig = SUPPORTED_NETWORKS_ETHEREUM[defaultEthereumChainId];

export const DEFAULT_EVM_CONFIG: EthereumControllerConfig = {
  AccountTrackerConfig: {},
  AccountAbstractionControllerConfig: {},
  CurrencyControllerConfig: { api: appConfig.api, pollInterval: 600_000 },
  NetworkControllerConfig: {
    providerConfig: defaultEthereumProviderConfig,
    pollingInterval: 30_000,
    blockResetDuration: 120_000,
  },
  PreferencesControllerConfig: {
    pollInterval: 180_000,
    api: appConfig.api,
    commonApiHost: appConfig.commonApiHost,
    authApi: CITADEL_SERVER_MAP[appConfig.buildEnv as BUILD_ENV_TYPE],
  },
  TokensControllerConfig: {
    interval: 180_000,
    selectedAddress: "",
    chainId: defaultEthereumChainId,
  },
  TokenRatesControllerConfig: {
    tokens: [],
    currencyApi: appConfig.api,
    pollInterval: 180_000,
    chainId: defaultEthereumChainId,
    nativeCurrency: defaultEthereumProviderConfig.ticker,
    selectedAddress: "",
  },
  NftsControllerConfig: {
    interval: 180_000,
    selectedAddress: "",
    chainId: defaultEthereumChainId,
  },
  KeyringControllerConfig: {},
  TransactionControllerConfig: { txHistoryLimit: 20 },
  GasFeeControllerConfig: {
    legacyAPIEndpoint: "https://gas-api.metaswap.codefi.network/networks/<chain_id>/gasPrices",
    EIP1559APIEndpoint: "https://gas-api.metaswap.codefi.network/networks/<chain_id>/suggestedGasFees",
    interval: 60_000,
  },
  EmbedControllerConfig: {},
  AddChainControllerConfig: {},
  DecryptMessageControllerConfig: {},
  EncryptionPublicKeyControllerConfig: {},
  MessageControllerConfig: {},
  PersonalMessageControllerConfig: {},
  SwitchChainControllerConfig: {},
  TypedMessageControllerConfig: {},
};

export const DEFAULT_EVM_STATE: EthereumControllerState = {
  AccountTrackerState: { accounts: {} },
  AccountAbstractionControllerState: {
    smartAccountAddress: "",
    userOperations: {},
  },
  CurrencyControllerState: {
    conversionDate: Date.now().toString(),
    conversionRate: 0,
    currentCurrency: "usd",
    nativeCurrency: "eth",
    ticker: "eth",
  },
  NetworkControllerState: {
    chainId: defaultEthereumChainId,
    properties: {},
    providerConfig: { ...defaultEthereumProviderConfig, isTestnet: defaultEthereumProviderConfig.isTestnet || false },
  },
  KeyringControllerState: { wallets: [] },
  PreferencesControllerState: {
    identities: {},
    selectedAddress: "",
  },
  EmbedControllerState: {
    isIFrameFullScreen: false,
    oauthModalVisibility: false,
    loginInProgress: false,
    dappMetadata: {
      name: "",
      icon: "",
    },
    web3AuthClientId: null,
    web3AuthNetwork: null,
    authConnectionConfig: undefined,
    loginMethodConfig: {},
    showWalletConnect: { show: false },
    showFunding: { show: false },
    showCheckout: { show: false },
    showReceive: { show: false },
    showWalletUi: { show: false },
    showSwap: { show: false },
    whiteLabel: null,
    windowBlockAlert: [],
    confirmationStrategy: CONFIRMATION_STRATEGY.DEFAULT,
    chainConfig: defaultEthereumProviderConfig, // TODO: remove this after controllers deprecate chainConfig in a breaking version
    gating: {
      allowWalletService: true,
      errorMessage: "",
    },
    enableKeyExport: false,
  },
  TokensControllerState: {
    tokens: {},
  },
  TokenRatesControllerState: {
    contractExchangeRates: {},
  },
  NftsControllerState: {
    nfts: {},
  },
  TransactionControllerState: {
    transactions: {},
  },
  GasFeeControllerState: {
    estimatedGasFeeTimeBounds: {},
    gasFeeEstimates: {},
    gasEstimateType: GAS_ESTIMATE_TYPES.NONE,
  },
  PersonalMessageControllerState: {
    unapprovedMessages: {},
    unapprovedMessagesCount: 0,
  },
  TypedMessageControllerState: {
    unapprovedMessages: {},
    unapprovedMessagesCount: 0,
  },
  MessageControllerState: {
    unapprovedMessages: {},
    unapprovedMessagesCount: 0,
  },
  SwitchChainControllerState: {
    unapprovedMessages: {},
    unapprovedMessagesCount: 0,
  },
  AddChainControllerState: {
    unapprovedMessages: {},
    unapprovedMessagesCount: 0,
  },
};

export const DEFAULT_SOLANA_CONFIG: SolanaControllerConfig = {
  NetworkControllerConfig: {
    providerConfig: defaultSolanaProviderConfig,
    pollingInterval: 30000,
    blockResetDuration: 120000,
  },
  KeyringControllerConfig: {},
  CurrencyControllerConfig: { api: appConfig.api, pollInterval: 600000 },
  PreferencesControllerConfig: {
    pollInterval: 180000,
    api: appConfig.api,
    commonApiHost: appConfig.commonApiHost,
    authApi: CITADEL_SERVER_MAP[appConfig.buildEnv as BUILD_ENV_TYPE],
  },
  AccountTrackerConfig: {},
  TokensControllerConfig: {
    chainId: defaultSolanaChainId,
    interval: 180000,
    supportedCurrencies: [],
    api: appConfig.api,
  },
  TokenRatesControllerConfig: {
    tokens: [],
    currencyApi: appConfig.api,
    pollInterval: 180000,
    chainId: defaultSolanaChainId,
    nativeCurrency: defaultSolanaProviderConfig.ticker,
    selectedAddress: "",
  },
  NftsControllerConfig: {
    interval: 180_000,
    selectedAddress: "",
    chainId: defaultSolanaChainId,
  },
  EmbedControllerConfig: {},
  TransactionControllerConfig: {
    txHistoryLimit: 20,
  },
  MessageControllerConfig: {},
  SwitchChainControllerConfig: {},
};

export const DEFAULT_SOLANA_STATE: SolanaControllerState = {
  NetworkControllerState: {
    chainId: appConfig.defaultChainIds[CHAIN_NAMESPACES.SOLANA],
    properties: {},
    providerConfig: {
      ...defaultSolanaProviderConfig,
      isTestnet: defaultSolanaProviderConfig?.isTestnet || false,
    },
    network: "loading",
    isCustomNetwork: false,
  },
  KeyringControllerState: { wallets: [] },
  CurrencyControllerState: {
    conversionDate: Date.now().toString(),
    conversionRate: 0,
    currentCurrency: "usd",
    nativeCurrency: "sol",
    ticker: "sol",
  },
  PreferencesControllerState: {
    identities: {},
    selectedAddress: "",
  },
  AccountTrackerState: { accounts: {} },
  TokenRatesControllerState: {
    contractExchangeRates: {},
  },
  TokensControllerState: {
    tokens: {},
    unknownSPLTokenInfo: [],
  },
  NftsControllerState: {
    nfts: {},
    unknownNFTAddresses: [],
  },
  EmbedControllerState: {
    isIFrameFullScreen: false,
    oauthModalVisibility: false,
    loginInProgress: false,
    dappMetadata: {
      name: "",
      icon: "",
    },
    web3AuthClientId: null,
    web3AuthNetwork: null,
    authConnectionConfig: undefined,
    showWalletConnect: { show: false },
    showFunding: { show: false },
    showCheckout: { show: false },
    showReceive: { show: false },
    showWalletUi: { show: false },
    showSwap: { show: false },
    whiteLabel: null,
    windowBlockAlert: [],
    confirmationStrategy: CONFIRMATION_STRATEGY.DEFAULT,
    loginMethodConfig: {},
    chainConfig: defaultSolanaProviderConfig, // TODO: remove this after controllers deprecate chainConfig in a breaking version
    gating: {
      allowWalletService: true,
      errorMessage: "",
    },
    enableKeyExport: false,
  },
  TransactionControllerState: {
    transactions: {},
  },
  MessageControllerState: {
    unapprovedMessages: {},
    unapprovedMessagesCount: 0,
  },
  SwitchChainControllerState: {
    unapprovedMessages: {},
    unapprovedMessagesCount: 0,
  },
};

export const DEFAULT_STATE: Record<string, ITorusControllerState> = {
  [CHAIN_NAMESPACES.EIP155]: DEFAULT_EVM_STATE,
  [CHAIN_NAMESPACES.SOLANA]: DEFAULT_SOLANA_STATE,
};

export const DEFAULT_CONFIG: Record<string, ITorusControllerConfig> = {
  [CHAIN_NAMESPACES.EIP155]: DEFAULT_EVM_CONFIG,
  [CHAIN_NAMESPACES.SOLANA]: DEFAULT_SOLANA_CONFIG,
};

export default class ControllerFactory {
  private controllerMap = new Map<ChainNamespaceType, ITorusController>();

  async init({
    chainNamespace,
    config,
    state,
    switchChain,
    getAccountAbstractionMultiChainConfig,
  }: {
    chainNamespace: ChainNamespaceType;
    config: ITorusControllerConfig;
    state: ITorusControllerState;
    switchChain: (chainId: string) => void;
    getAccountAbstractionMultiChainConfig?: () => AccountAbstractionMultiChainConfig | undefined;
  }) {
    const idleTimeTracker = createIdleTimeTracker();
    switch (chainNamespace) {
      case CHAIN_NAMESPACES.EIP155: {
        const { default: EthereumController } = await import("./EthereumController");
        this.controllerMap.set(
          chainNamespace,
          new EthereumController({
            config: config as Partial<EthereumControllerConfig>,
            state: state as Partial<EthereumControllerState>,
            idleTimeTracker,
            switchChain,
            getAccountAbstractionMultiChainConfig,
          })
        );
        break;
      }
      case CHAIN_NAMESPACES.SOLANA: {
        const { default: SolanaController } = await import("./SolanaController");
        this.controllerMap.set(
          chainNamespace,
          new SolanaController({
            config: config as Partial<SolanaControllerConfig>,
            state: state as Partial<SolanaControllerState>,
            idleTimeTracker,
            switchChain,
          })
        );
        break;
      }
      default: {
        throw new Error(`Unsupported chain namespace: ${chainNamespace}`);
      }
    }
    const currentController = this.controllerMap.get(chainNamespace);
    currentController.init({ config, state });
    return currentController;
  }

  public getController(chainNamespace: ChainNamespaceType): ITorusController {
    return this.controllerMap.get(chainNamespace);
  }
}
